/**
 * AddSecondParentPrompt
 *
 * Member-portal nudge: "add a second parent or guardian so we know who to
 * greet." Shown to a MEMBER looking at their own account when the account has
 * no additional contact at all.
 *
 * Renders nothing when:
 *   - the signed-in person is staff or an admin (staff looking up an account
 *     after hours must not get a pop-up — the nudge belongs to the family);
 *   - the signed-in person is a secondary user on the account (they ARE the
 *     second adult);
 *   - contacts have not loaded (unknown is not "none");
 *   - the account already has an additional contact;
 *   - the member dismissed it in the last 30 days.
 *
 * The dismissal is a per-browser convenience (localStorage), keyed by
 * membership, so it survives sign-out but never blocks anything: if storage is
 * unavailable the prompt simply shows again.
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Users, X } from "lucide-react";
import { Button, Card } from "../ui";
import { getTenantId } from "../../api/apiHelpers";
import { storedRoleIsAdminOrStaff } from "../../utils/roles";
import { useTheme } from "../../context/ThemeContext";
import { getPrimaryText, getSecondaryText } from "../../utils/themeUtils";
import { buttonClasses } from "../../utils/designTokens";

export const SECOND_PARENT_PROMPT_SNOOZE_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

const storageKey = (membershipId: string) => `secondParentPromptDismissedAt:${membershipId}`;

/** True while a dismissal is still inside the snooze window. */
export const isSecondParentPromptSnoozed = (membershipId: string, now: number = Date.now()): boolean => {
  try {
    const raw = localStorage.getItem(storageKey(membershipId));
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return now - at < SECOND_PARENT_PROMPT_SNOOZE_DAYS * DAY_MS;
  } catch {
    return false;
  }
};

interface MembershipLike {
  id?: string;
  additional_contacts?: unknown;
  __caller_role?: string;
}

interface Props {
  membership: MembershipLike | null | undefined;
}

const AddSecondParentPrompt: React.FC<Props> = ({ membership }) => {
  const { isDarkMode } = useTheme();
  const membershipId = membership?.id ? String(membership.id) : "";
  const [dismissed, setDismissed] = useState<boolean>(() =>
    membershipId ? isSecondParentPromptSnoozed(membershipId) : false
  );

  if (!membershipId) return null;
  if (storedRoleIsAdminOrStaff()) return null;
  if (membership?.__caller_role === "secondary") return null;

  const contacts = membership?.additional_contacts;
  // Unknown is not "none": only prompt when we KNOW the list is empty.
  if (!Array.isArray(contacts) || contacts.length > 0) return null;
  if (dismissed || isSecondParentPromptSnoozed(membershipId)) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(storageKey(membershipId), String(Date.now()));
    } catch {
      /* private mode / quota — the prompt just shows again next visit */
    }
    setDismissed(true);
  };

  const tenantId = getTenantId();

  return (
    <Card padding="md" className="mb-4 sm:mb-6" role="status" aria-label="Add a second parent or guardian">
      <div className="flex items-start gap-3">
        <Users className="w-5 h-5 mt-0.5 flex-shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${getPrimaryText(isDarkMode)}`}>
            Is there a second parent or guardian?
          </p>
          <p className={`text-sm mt-1 ${getSecondaryText(isDarkMode)}`}>
            Add their name and phone number so our staff know who they are when they call or come
            in.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {/* A link, not a <Button> inside a link (a button nested in an
                anchor is invalid markup). Same look as the primary md Button. */}
            <Link
              to={`/${tenantId}/member/manage-profile`}
              className={buttonClasses("primary", "md")}
            >
              Add a parent or guardian
            </Link>
            <Button variant="secondary" size="md" onClick={dismiss}>
              Not now
            </Button>
          </div>
        </div>
        <Button
          type="button"
          variant="neutral"
          size="xs"
          iconOnly
          onClick={dismiss}
          aria-label="Dismiss"
          className="flex-shrink-0"
          icon={<X className="w-4 h-4" aria-hidden="true" />}
        />
      </div>
    </Card>
  );
};

export default AddSecondParentPrompt;
